import React, { useState } from 'react';
import { X, Loader2, AlertTriangle } from 'lucide-react';
import { consumeCredits, refundCredit, logFloorPlanExport } from '../../services/supabase';
import { exportFloorPlan } from '../../services/exportFloorPlan';
import { useCanvasStore } from '../../store/canvasStore';
import { EXPORT_CREDITS, type ExportFormat } from '../../types';
import type { FloorPlan } from '../../types';
import type { UserProfile } from '../../types';

interface ExportModalProps {
  floorPlan: FloorPlan;
  user: UserProfile;
  onClose: () => void;
  onSuccess: (creditsUsed: number) => void;
}

const FORMATS: { id: ExportFormat; label: string; desc: string }[] = [
  { id: 'png', label: 'PNG', desc: 'High-res, no watermark' },
  { id: 'pdf', label: 'PDF', desc: 'A4, print-ready' },
  { id: 'svg', label: 'SVG', desc: 'Vector, editable' },
];

const ExportModal: React.FC<ExportModalProps> = ({ floorPlan, user, onClose, onSuccess }) => {
  const [format, setFormat] = useState<ExportFormat>('png');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const credits = EXPORT_CREDITS[format];

  const handleExport = async () => {
    setError(null);
    if (user.credits < credits) {
      setError('Insufficient credits. Please top up.');
      return;
    }

    const shapes = useCanvasStore.getState().shapes;
    const drawable = shapes.filter(
      (s) => s.type === 'room' || s.type === 'symbol' || (s.points != null && s.points.length >= 4)
    );
    if (drawable.length === 0) {
      setError('Nothing to export — draw at least one room or wall first.');
      return;
    }

    setExporting(true);
    try {
      const result = await consumeCredits(user.id, credits);
      if (result !== 'success') {
        if (result === 'insufficient_credits') setError('Insufficient credits.');
        else setError('Transaction failed. Please try again.');
        setExporting(false);
        return;
      }

      try {
        await exportFloorPlan(shapes, format, floorPlan.name);
      } catch (renderErr) {
        // Render/download failed after charging — refund the credits.
        for (let i = 0; i < credits; i++) await refundCredit(user.id);
        setError(renderErr instanceof Error ? renderErr.message : 'Export failed.');
        setExporting(false);
        return;
      }

      await logFloorPlanExport(floorPlan.id, user.id, format, credits);
      onSuccess(credits);
      onClose();
    } catch (err) {
      setError('Export failed.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className="max-w-md w-full rounded-2xl p-6"
        style={{ backgroundColor: '#1c1c28', border: '1px solid #2e2e40' }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold" style={{ color: '#f0f0f5' }}>
            Export Floor Plan
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5" style={{ color: '#8888aa' }}>
            <X size={18} />
          </button>
        </div>

        <p className="text-sm mb-4" style={{ color: '#8888aa' }}>
          Choose your format:
        </p>
        <div className="space-y-2 mb-6">
          {FORMATS.map((f) => (
            <label
              key={f.id}
              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border ${
                format === f.id ? 'border-[#FF4C4C]' : 'border-transparent'
              }`}
              style={{ backgroundColor: format === f.id ? 'rgba(255,76,76,0.1)' : '#252532' }}
            >
              <input
                type="radio"
                name="format"
                checked={format === f.id}
                onChange={() => setFormat(f.id)}
                className="sr-only"
              />
              <span className="font-medium" style={{ color: '#f0f0f5' }}>{f.label}</span>
              <span className="text-sm flex-1" style={{ color: '#8888aa' }}>{f.desc}</span>
              <span className="text-sm font-mono" style={{ color: '#FF4C4C' }}>{EXPORT_CREDITS[f.id]} credits</span>
            </label>
          ))}
        </div>

        <p className="text-sm mb-4" style={{ color: '#8888aa' }}>
          You have <span className="font-semibold" style={{ color: '#f0f0f5' }}>{user.credits}</span> credits remaining
        </p>

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg mb-4" style={{ backgroundColor: 'rgba(239,68,68,0.2)', color: '#f87171' }}>
            <AlertTriangle size={18} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg font-medium"
            style={{ color: '#8888aa', backgroundColor: '#252532' }}
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={exporting || user.credits < credits}
            className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: '#FF4C4C', color: '#fff' }}
          >
            {exporting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Exporting...
              </>
            ) : (
              `Export — use ${credits} credits`
            )}
          </button>
        </div>

        <p className="text-xs mt-4 text-center" style={{ color: '#666' }}>
          Need more credits?{' '}
          <a href="#" className="underline" style={{ color: '#FF4C4C' }}>
            Top up
          </a>
        </p>
      </div>
    </div>
  );
};

export default ExportModal;
